import React, { useState, useEffect, useRef } from 'react';
import rentalApi from '../services/api';
import { GTA_CITIES } from '../types';
import { AddressAutocomplete } from './AddressAutocomplete';
import './FormShared.css';

const PROP_TYPES: { label: string; icon: string }[] = [
  { label: 'Basement',      icon: '🏚' },
  { label: 'Apartment',     icon: '🏢' },
  { label: 'Condo',         icon: '🏙' },
  { label: 'Townhouse',     icon: '🏘' },
  { label: 'Semi-Detached', icon: '🏠' },
  { label: 'Detached',      icon: '🏡' },
];
const BHK_OPTS  = ['1', '1+', '2', '2+', '3+'];
const TIMELINE  = ['ASAP', 'This Month', 'Next Month', 'Flexible'];
const FOOD      = ['Veg', 'Non-Veg', 'Any'];
const EMP       = ['Student', 'Employee', 'Other'];
const SMOKE     = ['Smoker', 'Non-Smoker', 'No Preference'];
const BATH_T    = ['Shared', 'Private'];
const GENDER    = ['Male', 'Female', 'Prefer Not to Say'];
const FURNISH   = ['Furnished', 'Semi-Furnished', 'Unfurnished'];
const UTILITIES = ['Hydro', 'Internet', 'Water'];
const AMENITIES = ['🏊 Pool', '🏋 Gym', '🚗 Parking', '🧖 Sauna'];
const PETS      = ['Yes', 'No', 'Not Sure'];
const TRANSIT   = ['0–5 km', '5–10 km', '10+ km'];

interface Props { onSuccess: () => void; onCancel: () => void; onCoordinatesChange?: (c: [number, number]) => void; externalCoords?: [number, number]; onOpenPrivacy?: () => void; onOpenTerms?: () => void; }

export const SeekRoomForm: React.FC<Props> = ({ onSuccess, onCancel, onCoordinatesChange, externalCoords, onOpenPrivacy, onOpenTerms }) => {
  const [address, setAddress]       = useState('');
  const [coords, setCoords]         = useState<[number, number] | null>(null);
  const [city, setCity]             = useState('Toronto');
  const [budget, setBudget]         = useState('');
  const [bhk, setBhk]               = useState<number | string | null>(null);
  const [propTypes, setPropTypes]   = useState<string[]>([]);
  const [timeline, setTimeline]     = useState('');
  const [food, setFood]             = useState('');
  const [empStatus, setEmpStatus]   = useState('');
  const [smoking, setSmoking]       = useState('');
  const [bathType, setBathType]     = useState('');
  const [youAre, setYouAre]         = useState('');
  const [comfWith, setComfWith]     = useState('');
  const [furnishing, setFurnishing] = useState('');
  const [utilities, setUtilities]   = useState<string[]>([]);
  const [amenities, setAmenities]   = useState<string[]>([]);
  const [pets, setPets]             = useState('');
  const [transit, setTransit]       = useState('');
  const [schoolRating, setSchoolRating] = useState(0);
  const [description, setDescription]  = useState('');
  const [lifestyle, setLifestyle]   = useState('');
  const [email, setEmail]           = useState('');
  const [phone, setPhone]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentAt, setConsentAt]   = useState<string | null>(null);
  const [submitted, setSubmitted]   = useState<{ link: string } | null>(null);
  const [copied, setCopied]         = useState(false);

  const coordsFromAutocompleteRef = useRef('');

  useEffect(() => {
    if (!externalCoords) return;
    const [lng, lat] = externalCoords;
    setCoords(externalCoords);
    const key = `${lng.toFixed(6)},${lat.toFixed(6)}`;
    if (coordsFromAutocompleteRef.current === key) return;
    if (!window.google?.maps) return;
    new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const r = results[0];
        setAddress(r.formatted_address);
        const comps = r.address_components ?? [];
        const cityComp = comps.find(c => c.types.includes('locality'));
        if (cityComp) setCity(cityComp.long_name);
      }
    });
  }, [externalCoords]);

  const toggleChip = (setArr: React.Dispatch<React.SetStateAction<string[]>>, val: string) =>
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const handleSubmit = async () => {
    setError('');
    if (!address) return setError('Please search and select a preferred area');
    if (coords) {
      const [lng, lat] = coords;
      if (lat < 43.1 || lat > 44.5 || lng < -80.7 || lng > -78.4) return setError("Sorry, we don't operate in this zone yet.");
    }
    if (city && !GTA_CITIES.includes(city)) return setError("Sorry, we don't operate in this zone yet.");
    if (!budget || parseFloat(budget) <= 0) return setError('Max budget per room is required');
    if (parseFloat(budget) > 9999) return setError('Budget cannot exceed $9,999');
    if (propTypes.length === 0) return setError('Select at least one Property Type');
    if (!youAre) return setError('Please select who you are under "About you"');
    if (!email) return setError('Email is required to match with listings');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Enter a valid email address');
    if (!termsAccepted) return setError('Please accept the Terms of Use and Privacy Policy to continue');
    setLoading(true);
    try {
      const res = await rentalApi.createRentalSearch({
        searchType: 'LookingForRoom',
        address: address || undefined,
        location: coords ? { coordinates: coords } : undefined,
        city: city || 'Toronto',
        budget: parseFloat(budget),
        bhkPreference: bhk === null ? undefined : parseInt(String(bhk)),
        propertyType: propTypes.join(','),
        moveInTimeline: timeline,
        foodPreference: food,
        employmentStatus: empStatus || undefined,
        smokingHabits: smoking,
        bathType, youAre: youAre || undefined,
        comfortableWith: comfWith || 'Any',
        furnishing: furnishing || undefined,
        amenities: amenities.length ? amenities : undefined,
        utilitiesIncluded: utilities.length ? utilities : undefined,
        petsAllowedStatus: pets,
        transitDistance: transit || undefined,
        schoolRating: schoolRating || undefined,
        description: description || undefined,
        lifestyle: lifestyle || undefined,
        email, phone: phone || undefined,
        consentAcceptedAt: consentAt ?? undefined,
      });
      const { id } = res.data.data;
      const token = res.data.deleteToken;
      const link = id && token
        ? `${window.location.origin}/?searchId=${id}&deleteToken=${token}`
        : '';
      setSubmitted({ link });
    } catch (err: any) {
      setError(err.message || 'Failed to post search. Please try again.');
    } finally { setLoading(false); }
  };

  const Tgl = ({ opts, val, onSet, labelId }: { opts: (string | number)[], val: any, onSet: (v: any) => void; labelId: string }) => (
    <div className="toggle-group" role="group" aria-labelledby={labelId}>
      {opts.map(o => (
        <button type="button" key={String(o)} className={`tgl${val === o ? ' on' : ''}`} aria-pressed={val === o} onClick={() => onSet(o)}>{o}</button>
      ))}
    </div>
  );

  const Chips = ({ opts, sel, setSel, labelId }: { opts: string[], sel: string[], setSel: React.Dispatch<React.SetStateAction<string[]>>; labelId: string }) => (
    <div className="chip-group" role="group" aria-labelledby={labelId}>
      {opts.map(o => (
        <button type="button" key={o} className={`chip${sel.includes(o) ? ' on' : ''}`} aria-pressed={sel.includes(o)} onClick={() => toggleChip(setSel, o)}>{o}</button>
      ))}
    </div>
  );

  const sectionsComplete = [
    !!address,
    !!budget && propTypes.length > 0,
    !!youAre,
    true,
    !!email,
  ].filter(Boolean).length;

  if (submitted) {
    const copyLink = () => {
      navigator.clipboard.writeText(submitted.link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    return (
      <div className="form-panel">
        <div className="form-success">
          <div className="form-success-icon">✓</div>
          <h3>Search posted!</h3>
          <p>Save this link to remove your search at any time.</p>
          {submitted.link && (
            <>
              <div className="form-success-link-row">
                <input className="form-success-link-input" readOnly value={submitted.link} />
                <button className={`form-success-copy${copied ? ' copied' : ''}`} onClick={copyLink}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="form-success-note">Anyone with this link can delete this search. Keep it safe.</p>
            </>
          )}
          <button className="form-success-done" onClick={onSuccess}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-panel">
      <div className="form-panel-header">
        <div className="form-panel-eyebrow">
          <span>Seek a Room</span>
          <button type="button" className="form-panel-close" onClick={onCancel}>✕</button>
        </div>
        <h2 className="form-panel-title">Find your ideal roommate setup</h2>
        <p className="form-panel-sub">Tell us what you're looking for and we'll connect you.</p>
      </div>

      <div className="form-panel-body">
        {error && <div className="err">{error}</div>}

        {/* 01 Where to look */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true"><span className="form-section-num">01</span><span className="form-section-title">Where to look?</span></div>
          <div className="fs">
            <label htmlFor="srf-address" className="fs-label">Preferred area <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></label>
            <AddressAutocomplete inputId="srf-address" value={address} onChange={setAddress}
              onPlaceSelect={(addr, c, ct) => { coordsFromAutocompleteRef.current = `${c[0].toFixed(6)},${c[1].toFixed(6)}`; setAddress(addr); setCoords(c); if (ct) setCity(ct); onCoordinatesChange?.(c); }}
              onClear={() => { setAddress(''); setCoords(null); }}
              placeholder="e.g. Scarborough, Yonge & Eglinton…" aria-required="true" />
            {coords && address && (
              <div className="addr-confirm" role="status">
                <span className="addr-confirm-dot" aria-hidden="true" />
                {address.split(',').slice(0, 2).join(',')}
              </div>
            )}
          </div>
          <div className="fs"><span id="srf-timeline-label" className="fs-label">Move-in timeline</span><Tgl opts={TIMELINE} val={timeline} onSet={setTimeline} labelId="srf-timeline-label" /></div>
        </div>

        {/* 02 What you need */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true"><span className="form-section-num">02</span><span className="form-section-title">What you need</span></div>
          <div className="fs">
            <label htmlFor="srf-budget" className="fs-label">Max budget / room / mo <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></label>
            <div className="rent-input-row">
              <span className="rent-prefix" aria-hidden="true">$</span>
              <input id="srf-budget" className="rent-input" type="number" value={budget}
                onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) <= 9999) setBudget(v); }}
                placeholder="900" min="1" max="9999" aria-required="true" />
              <span className="rent-suffix" aria-hidden="true">/mo</span>
            </div>
          </div>
          <div className="fs">
            <span id="srf-proptype-label" className="fs-label">Property Type <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span> <span className="fs-label-opt">select all that apply</span></span>
            <div className="proptype-grid" role="group" aria-labelledby="srf-proptype-label">
              {PROP_TYPES.map(pt => (
                <button type="button" key={pt.label} className={`proptype-tile${propTypes.includes(pt.label) ? ' on' : ''}`}
                  aria-pressed={propTypes.includes(pt.label)} onClick={() => toggleChip(setPropTypes, pt.label)}>
                  <span className="proptype-tile-icon" aria-hidden="true">{pt.icon}</span><span>{pt.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="fs">
            <span id="srf-bhk-label" className="fs-label">BHK in home</span>
            <div className="bhk-group" role="group" aria-labelledby="srf-bhk-label">
              {BHK_OPTS.map(o => (
                <button type="button" key={String(o)} className={`bhk-tile${bhk === o ? ' on' : ''}`} aria-pressed={bhk === o} onClick={() => setBhk(o)}>{o}</button>
              ))}
            </div>
          </div>
          <div className="fs"><span id="srf-bath-label" className="fs-label">Bath <span className="fs-label-opt">optional</span></span><Tgl opts={BATH_T} val={bathType} onSet={v => setBathType(bathType === v ? '' : v)} labelId="srf-bath-label" /></div>
          <div className="fs"><span id="srf-furnish-label" className="fs-label">Furnishing</span><Tgl opts={FURNISH} val={furnishing} onSet={v => setFurnishing(furnishing === v ? '' : v)} labelId="srf-furnish-label" /></div>
        </div>

        {/* 03 About you */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true"><span className="form-section-num">03</span><span className="form-section-title">About you</span></div>
          <div className="fs"><span id="srf-youare-label" className="fs-label">You are <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></span><Tgl opts={GENDER} val={youAre} onSet={v => setYouAre(youAre === v ? '' : v)} labelId="srf-youare-label" /></div>
          <div className="fs"><span id="srf-comf-label" className="fs-label">Comfortable with flatmate</span><Tgl opts={GENDER} val={comfWith} onSet={v => setComfWith(comfWith === v ? '' : v)} labelId="srf-comf-label" /></div>
          <div className="fs"><span id="srf-emp-label" className="fs-label">Employment</span><Tgl opts={EMP} val={empStatus} onSet={v => setEmpStatus(empStatus === v ? '' : v)} labelId="srf-emp-label" /></div>
          <div className="fs"><span id="srf-food-label" className="fs-label">Food preference</span><Tgl opts={FOOD} val={food} onSet={setFood} labelId="srf-food-label" /></div>
          <div className="fs"><span id="srf-smoke-label" className="fs-label">Smoking / Drinking</span><Tgl opts={SMOKE} val={smoking} onSet={setSmoking} labelId="srf-smoke-label" /></div>
          <div className="fs">
            <label htmlFor="srf-lifestyle" className="fs-label">Lifestyle <span className="fs-label-opt">optional · 200 words</span></label>
            <textarea id="srf-lifestyle" className="fi" rows={2} value={lifestyle}
              onChange={e => { const words = e.target.value.trim().split(/\s+/).filter(Boolean); if (words.length <= 200) setLifestyle(e.target.value); }}
              placeholder="Early riser, clean, WFH…" aria-describedby="srf-lifestyle-cc" />
            <span id="srf-lifestyle-cc" className="cc" aria-live="polite">{lifestyle.trim() ? lifestyle.trim().split(/\s+/).filter(Boolean).length : 0}/200 words</span>
          </div>
        </div>

        {/* 04 Nice to haves */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true"><span className="form-section-num">04</span><span className="form-section-title">Nice to haves</span></div>
          <div className="fs"><span id="srf-util-label" className="fs-label">Utilities preferred</span><Chips opts={UTILITIES} sel={utilities} setSel={setUtilities} labelId="srf-util-label" /></div>
          <div className="fs"><span id="srf-amen-label" className="fs-label">Amenities</span><Chips opts={AMENITIES} sel={amenities} setSel={setAmenities} labelId="srf-amen-label" /></div>
          <div className="fs"><span id="srf-pets-label" className="fs-label">Pets</span><Tgl opts={PETS} val={pets} onSet={setPets} labelId="srf-pets-label" /></div>
          <div className="fs"><span id="srf-transit-label" className="fs-label">Transit</span><Tgl opts={TRANSIT} val={transit} onSet={v => setTransit(transit === v ? '' : v)} labelId="srf-transit-label" /></div>
          <div className="fs">
            <label htmlFor="srf-school-input" className="fs-label">School rating preference</label>
            <div className="school-rating-row">
              <input
                type="range"
                id="srf-school-input"
                className="school-rating-slider"
                min={0} max={10} step={1}
                value={schoolRating}
                onChange={e => setSchoolRating(Number(e.target.value))}
                aria-valuetext={schoolRating === 0 ? 'No preference' : `${schoolRating} out of 10`}
              />
              <span className="school-rating-badge" aria-live="polite">
                {schoolRating === 0 ? 'Any' : `${schoolRating}/10`}
              </span>
            </div>
            <div className="school-rating-scale">
              <span>0 · No preference</span><span>10 · Excellent</span>
            </div>
          </div>
        </div>

        {/* 05 Contact */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true"><span className="form-section-num">05</span><span className="form-section-title">Contact</span></div>
          <div className="fs">
            <label htmlFor="srf-desc" className="fs-label">One-liner <span className="fs-label-opt">optional · 200 chars</span></label>
            <textarea id="srf-desc" className="fi" rows={2} value={description} onChange={e => setDescription(e.target.value.slice(0, 200))} placeholder="Introduce yourself briefly…" aria-describedby="srf-desc-cc" />
            <span id="srf-desc-cc" className="cc" aria-live="polite">{description.length}/200</span>
          </div>
          <div className="g2">
            <div className="fs">
              <label htmlFor="srf-email" className="fs-label">Email <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></label>
              <input id="srf-email" className="fi" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" aria-required="true" />
            </div>
            <div className="fs">
              <label htmlFor="srf-phone" className="fs-label">Phone <span className="fs-label-opt">optional</span></label>
              <input id="srf-phone" className="fi" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 647…" />
            </div>
          </div>
        </div>

        <div className="consent-box">
          <label className="consent-label">
            <input type="checkbox" className="consent-check" checked={termsAccepted} aria-required="true"
              onChange={e => { setTermsAccepted(e.target.checked); setConsentAt(e.target.checked ? new Date().toISOString() : null); }} />
            <span className="consent-text">
              I agree to the{' '}
              <button type="button" className="consent-link" onClick={() => onOpenTerms?.()}>Terms of Use</button>
              {' '}and{' '}
              <button type="button" className="consent-link" onClick={() => onOpenPrivacy?.()}>Privacy Policy</button>
              . I understand my email is used only to manage this listing and will be deleted after 30 days of inactivity.
            </span>
          </label>
        </div>
      </div>

      <div className="form-actions">
        {error && <div className="err-footer" role="alert">{error}</div>}
        <span className="form-progress" aria-live="polite">{sectionsComplete}/5 · {budget ? 'ready' : 'add budget'}</span>
        <button type="button" className="btn-cancel" onClick={onCancel} disabled={loading}>Cancel</button>
        <button type="button" className="btn-submit" onClick={handleSubmit} disabled={loading} aria-busy={loading}>
          {loading ? 'Posting…' : '🔍 Seek Room'}
        </button>
      </div>
    </div>
  );
};
